import React, { ReactNode, useEffect, useRef, useState } from "react";
import { Button, Drawer, Dropdown } from "antd";
import { DsaScheduleDto } from "../../generated/dashboard-client";
import { DeleteOutlined, DownOutlined, EditOutlined, LockOutlined, MoreOutlined, PlusOutlined, UnlockOutlined } from "@ant-design/icons";
import { getTimeAgoFromString } from "../../util/utils";
import { memberState, mobileState } from "../../state/atoms";
import { useRecoilValue } from "recoil";
import { useOnClickOutside } from "usehooks-ts";

interface ScheduleCardProps {
  it: DsaScheduleDto;
  selected: boolean;
  selectSchedule: (schedule: DsaScheduleDto) => void;
  edit: (schedule: DsaScheduleDto) => void;
  toggleLock: (schedule: DsaScheduleDto) => void;
  deleteSchedule: (schedule: DsaScheduleDto) => void;
  createEvent: (schedule: DsaScheduleDto) => void;
  withTooltip: (tooltip: string, element: ReactNode) => ReactNode;
}

export default function ScheduleCard({ it, selected, selectSchedule, edit, toggleLock, deleteSchedule, createEvent, withTooltip }: ScheduleCardProps) {
  const member = useRecoilValue(memberState);
  const [lockedForUser, setLockedForUser] = useState(false);
  const [isMenuVisible, setMenuVisible] = useState(false);
  const menuRef = useRef(null);

  const mobile = useRecoilValue(mobileState);
  const [isMobileMenuVisible, setMobileMenuVisible] = useState(false);

  useEffect(() => {
    setLockedForUser(it.locked ? !member.owner : false);
  }, [member, it]);

  function hideDropdown() {
    setMenuVisible(false);
  }

  const handleClickOutside = (ev: MouseEvent) => {
    hideDropdown();
  }

  useOnClickOutside(menuRef, (ev) => handleClickOutside(ev as MouseEvent));

  function onEditClick() {
    hideDropdown();
    setMobileMenuVisible(false);
    edit(it);
  }

  function onToggleLockClick() {
    hideDropdown();
    setMobileMenuVisible(false);
    toggleLock(it);
  }

  function onDeleteClick() {
    hideDropdown();
    setMobileMenuVisible(false);
    deleteSchedule(it);
  }

  function onNewEventClick() {
    hideDropdown();
    setMobileMenuVisible(false);
    createEvent(it);
  }

  const menuContent = (<div ref={menuRef}>
      <div className="info">
        {mobile && <h3>{it.name!}</h3>}
        Created by {it.createdByName} &bull; Updated by {it.updatedByName} &bull; Edited {getTimeAgoFromString(it.updatedDate!)}
      </div>
      <ul style={{ margin: 0 }}>
        <li onClick={() => onEditClick()}><EditOutlined />Edit</li>
        <li onClick={() => onNewEventClick()}><PlusOutlined /> New Event</li>
        {member.owner && <li onClick={() => onToggleLockClick()}>
          {it.locked ?
            <><UnlockOutlined />Unlock Schedule</> :
            <><LockOutlined />Lock Schedule</>}
        </li>}
        <li onClick={() => onDeleteClick()}><DeleteOutlined />Delete</li>
      </ul>
    </div>)

  return (
  <li key={it.id} className="schedule-card">
    <div onClick={() => selectSchedule(it)} className={selected ? "sel" : ""}>
      <label>{it.name}</label>
    </div>
    {!lockedForUser && !mobile ?
      <Dropdown
        visible={isMenuVisible}
        placement="bottomRight"
        overlayClassName="playlist-edit-menu schedule-edit-menu"
        overlay={menuContent}
      >
        {withTooltip('More', <Button type={"link"}>
          <MoreOutlined onClick={() => { setMenuVisible(!isMenuVisible) }} />
        </Button>)}
      </Dropdown>
    : 
    !lockedForUser && mobile ? <>
      <Button type={"link"}>
        <MoreOutlined onClick={() => { setMobileMenuVisible(!isMobileMenuVisible) }} />
      </Button>
      <Drawer
        placement="bottom"
        visible={isMobileMenuVisible}
        className="mobile-bottom-menu playlist-edit-menu schedule-edit-menu"
        maskClosable={true}
        closeIcon={<DownOutlined />}
        onClose={() => setMobileMenuVisible(false)}
      >
        {menuContent}
      </Drawer>
    </>
    : <></>
    }
    {lockedForUser && withTooltip('Locked by owner', <LockOutlined />)}
  </li>);
}
